import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import SafeEnterpriseLogService from '../../services/safeEnterpriseLogService';
import SearchableSelect from '../common/SearchableSelect';
import api from '../../services/api';

const SystemLogs = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    level: '',
    channel: 'system', // Filter to system logs only 
    user_id: '',
    date_from: '',
    date_to: '',
    search: '',
    correlation_id: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });
  const [systemStats, setSystemStats] = useState(null);
  const [contextModal, setContextModal] = useState({ isOpen: false, context: null, logInfo: null });
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // System-specific categories to filter by
  const systemCategories = [
    'system',
    'authentication',
    'security',
    'database',
    'api',
    'configuration',
    'performance'
  ];

  // Fetch system logs with filters and pagination
  const fetchSystemLogs = async (page = 1) => {
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      
      const params = {
        page: page.toString(),
        limit: pagination.limit.toString(),
        // Force system channel filter
        channel: 'system',
        // Add other filters but prioritize system-related content
        ...filters,
        // Override channel to ensure we get system logs
        channel: filters.channel || 'system'
      };

      const data = await SafeEnterpriseLogService.getLogs(params);
      
      // Handle successful response
      if (data && data.status === 1) {
        // Use the logs directly from API since we're filtering on backend
        const logs = data.data?.data || [];
        
        setLogs(logs);
        setPagination(prev => ({
          ...prev,
          page: data.data?.meta?.current_page || 1,
          total: data.data?.meta?.total || 0,
          totalPages: data.data?.meta?.last_page || 1
        }));
      } else {
        // Handle API error response
        const errorMessage = data?.message || 'Failed to fetch system logs';
        console.error('API returned error:', errorMessage);
        setError(errorMessage);
        
        // Set empty data to prevent UI crashes
        setLogs([]);
        setPagination(prev => ({
          ...prev,
          page: 1,
          total: 0,
          totalPages: 1
        }));
      }
    } catch (err) {
      console.error('Error fetching system logs:', err);
      
      // Determine error message based on error type
      let errorMessage = 'Failed to fetch system logs';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.name === 'SyntaxError') {
        errorMessage = 'Invalid response from server - please try again';
      } else if (err.name === 'TypeError') {
        errorMessage = 'Network error - please check your connection';
      }
      
      setError(errorMessage);
      
      // Set empty data to prevent UI crashes
      setLogs([]);
      setPagination(prev => ({
        ...prev,
        page: 1,
        total: 0,
        totalPages: 1
      }));
    } finally {
      setLoading(false);
    }
  };

  // Fetch system statistics
  const fetchSystemStats = async () => {
    try {
      const data = await SafeEnterpriseLogService.getStatistics();
      
      if (data && data.status === 1) {
        setSystemStats(data.data);
      } else {
        // Handle API error response
        console.error('System stats API returned error:', data?.message || 'Unknown error');
        // Set default stats on error
        setSystemStats({
          active_users: 0,
          today_logs: 0,
          total_logs: 0
        });
      }
    } catch (err) {
      console.error('Error fetching system stats:', err);
      
      // Determine error message based on error type
      let errorMessage = 'Failed to fetch system statistics';
      if (err.message) {
        errorMessage = err.message;
      } else if (err.name === 'SyntaxError') {
        errorMessage = 'Invalid response from server - please try again';
      } else if (err.name === 'TypeError') {
        errorMessage = 'Network error - please check your connection';
      }
      
      console.error('System stats error:', errorMessage);
      
      // Set default stats on error
      setSystemStats({
        active_users: 0,
        today_logs: 0,
        total_logs: 0
      });
    }
  };

  // Fetch users for searchable select
  const fetchUsers = async () => {
    try {
      const allUsersData = await api.get('/api/users?pageSize=5000&limit=5000');

      if (allUsersData?.data) {
        let usersList = [];
        
        if (allUsersData.data.status === 1) {
          if (allUsersData.data.data && Array.isArray(allUsersData.data.data.rows)) {
            usersList = allUsersData.data.data.rows;
          } else if (allUsersData.data.data && Array.isArray(allUsersData.data.data)) {
            usersList = allUsersData.data.data;
          } else if (allUsersData.data.users && Array.isArray(allUsersData.data.users)) {
            usersList = allUsersData.data.users;
          }
        } else if (Array.isArray(allUsersData.data)) {
          usersList = allUsersData.data;
        }
        
        const userOptions = usersList.map(u => ({
          value: u.user_id,
          label: u.user_full_name || u.user_email || 'Unknown User',
          name: u.user_full_name || u.user_email || 'Unknown User',
          email: u.user_email,
          fullLabel: `${u.user_full_name || u.user_email || 'Unknown'} (${u.user_email || u.user_id})`
        }));
        setUsers(userOptions);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    // Only fetch data if user is authenticated
    if (user && user.user_id) {
      fetchSystemStats();
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    // Only fetch logs if user is authenticated and we have filters
    if (user && user.user_id) {
      fetchSystemLogs(1);
    }
  }, [filters, user]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleUserSelect = (selectedUser) => {
    setSelectedUser(selectedUser);
    handleFilterChange('user_id', selectedUser ? selectedUser.value : '');
  };

  const clearFilters = () => {
    setFilters({
      level: '',
      channel: 'system', 
      user_id: '',
      date_from: '',
      date_to: '',
      search: '',
      correlation_id: ''
    });
    setSelectedUser(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getLevelColor = (level) => {
    return SafeEnterpriseLogService.getLevelColor(level);
  };

  const formatContext = (context, logInfo = null) => {
    if (!context) return <span className="text-xs text-gray-500">No context</span>;
    
    try {
      const parsed = SafeEnterpriseLogService.formatContext(context);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        const totalItems = Object.keys(parsed).length;
        
        return (
          <div className="text-xs text-gray-600 max-w-xs">
            {Object.entries(parsed).slice(0, 3).map(([key, value]) => (
              <div key={key} className="truncate">
                <span className="font-medium">{key}:</span> {String(value || '')}
              </div>
            ))}
            {totalItems > 3 && (
              <button
                onClick={() => setContextModal({ isOpen: true, context: parsed, logInfo })}
                className="text-blue-500 hover:text-blue-700 cursor-pointer underline"
              >
                +{totalItems - 3} more...
              </button>
            )}
          </div>
        );
      }
      return <span className="text-xs text-gray-500">No context</span>;
    } catch (err) {
      console.error('Error formatting context:', err);
      return <span className="text-xs text-gray-500">Invalid context</span>;
    }
  };

  const formatMetadata = (metadata) => {
    if (!metadata) return <span className="text-xs text-gray-500">No metadata</span>;
    
    try {
      const parsed = typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        return (
          <div className="text-xs text-gray-600">
            {Object.entries(parsed).map(([key, value]) => (
              <div key={key} className="truncate">
                <span className="font-medium">{key}:</span> {String(value || '')}
              </div>
            ))}
          </div>
        );
      }
      return <span className="text-xs text-gray-500">No metadata</span>;
    } catch (err) {
      console.error('Error formatting metadata:', err);
      return <span className="text-xs text-gray-500">Invalid metadata</span>;
    }
  };

  const getSystemActionColor = (action) => {
    const colors = {
      login: 'bg-green-100 text-green-800',
      logout: 'bg-gray-100 text-gray-800',
      system: 'bg-blue-100 text-blue-800',
      auth: 'bg-purple-100 text-purple-800',
      security: 'bg-red-100 text-red-800',
      database: 'bg-yellow-100 text-yellow-800',
      api: 'bg-indigo-100 text-indigo-800',
      setting: 'bg-orange-100 text-orange-800',
      default: 'bg-gray-100 text-gray-800'
    };
    
    const lowerAction = action.toLowerCase();
    
    for (const [key, color] of Object.entries(colors)) {
      if (lowerAction.includes(key)) {
        return color;
      }
    }
    
    return colors.default;
  };

  const getLogSeverity = (log) => {
    const action = log.action.toLowerCase();
    if (action.includes('error') || action.includes('fail') || action.includes('security')) {
      return { level: 'ERROR', color: 'text-red-600' };
    }
    if (action.includes('warning') || action.includes('warn')) {
      return { level: 'WARN', color: 'text-yellow-600' };
    }
    if (action.includes('login') || action.includes('logout') || action.includes('auth')) {
      return { level: 'INFO', color: 'text-blue-600' };
    }
    return { level: 'DEBUG', color: 'text-gray-600' };
  };

  // Context Modal Component
  const ContextModal = () => {
    if (!contextModal.isOpen || !contextModal.context) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white">
          <div className="mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">System Log Context Details</h3>
              <button
                onClick={() => setContextModal({ isOpen: false, context: null, logInfo: null })}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Log Info */}
            {contextModal.logInfo && (
              <div className="mb-4 p-3 bg-gray-50 rounded-md">
                <div className="text-sm text-gray-600">
                  <strong>Timestamp:</strong> {formatDate(contextModal.logInfo.timestamp)}
                </div>
                <div className="text-sm text-gray-600">
                  <strong>Message:</strong> {contextModal.logInfo.message}
                </div>
              </div>
            )}

            {/* Context Data */}
            <div className="max-h-96 overflow-y-auto">
              <div className="space-y-2">
                {Object.entries(contextModal.context).map(([key, value]) => (
                  <div key={key} className="border-b border-gray-100 pb-2">
                    <div className="flex flex-col sm:flex-row">
                      <div className="w-full sm:w-1/3 font-medium text-gray-900 text-sm">
                        {key}:
                      </div>
                      <div className="w-full sm:w-2/3 text-sm text-gray-700 break-all">
                        {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setContextModal({ isOpen: false, context: null, logInfo: null })}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Professional Header Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0096DE" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  System Logs
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  Monitor system events, authentication, and security logs
                </p>
                {/* System Health Indicators */}
                <div className="flex items-center gap-4 mt-2">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">Database</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">API Services</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-xs text-gray-600">Authentication</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right: Statistics */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-lg font-semibold text-green-600">
                  Healthy
                </div>
                <div className="text-xs text-gray-500">System Status</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-gray-900">
                  {systemStats ? (systemStats.active_users || 0) : (loading ? '...' : '0')}
                </div>
                <div className="text-xs text-gray-500">Active Sessions</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-purple-600">
                  0
                </div>
                <div className="text-xs text-gray-500">Security Events</div>
              </div>
              <div className="text-center">
                <div className="text-lg font-semibold text-orange-600">
                  {systemStats ? (systemStats.today_logs || 0) : (loading ? '...' : '0')}
                </div>
                <div className="text-xs text-gray-500">Today's Events</div>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">System Log Filters</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Log Level</label>
                <select
                  value={filters.level}
                  onChange={(e) => handleFilterChange('level', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">All Levels</option>
                  <option value="emergency">Emergency</option>
                  <option value="alert">Alert</option>
                  <option value="critical">Critical</option>
                  <option value="error">Error</option>
                  <option value="warning">Warning</option>
                  <option value="notice">Notice</option>
                  <option value="info">Info</option>
                  <option value="debug">Debug</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                <SearchableSelect
                  items={users}
                  onSelectItem={handleUserSelect}
                  initialSelectedItem={selectedUser}
                  placeholder="Select user..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => handleFilterChange('date_from', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => handleFilterChange('date_to', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  placeholder="Search messages, contexts, categories..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Correlation ID</label>
                <input
                  type="text"
                  value={filters.correlation_id}
                  onChange={(e) => handleFilterChange('correlation_id', e.target.value)}
                  placeholder="Track related requests..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>

        {/* System Logs Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Enterprise System Log Entries</h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading system logs...</p>
            </div>
          ) : error ? (
            <div className="p-8 text-center">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
                <div className="flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading System Logs</h3>
                <p className="text-red-600 mb-4">{error}</p>
                <button
                  onClick={() => {
                    setError(null);
                    fetchSystemLogs(1);
                  }}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>No system logs found matching your criteria.</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Timestamp
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Level
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Channel
                      </th> 
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Message
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Context
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log, index) => {
                      // Safety check for log object
                      if (!log || typeof log !== 'object') {
                        console.warn('Invalid log object at index', index, log);
                        return null;
                      }
                      
                      return (
                        <tr key={log.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.timestamp ? formatDate(log.timestamp) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getLevelColor(log.level)}`}>
                              {log.level ? log.level.charAt(0).toUpperCase() + log.level.slice(1) : 'Info'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <span className="bg-blue-100 text-blue-800 px-2 py-1 text-xs rounded-full">
                              {log.channel || 'system'}
                            </span>
                          </td> 
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-md">
                            <div className="truncate" title={log.message || ''}>
                              {log.message || 'No message'}
                            </div>
                            {log.correlation_id && (
                              <div className="text-xs text-gray-500 mt-1">
                                ID: {String(log.correlation_id).substring(0, 8)}...
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                            {formatContext(log.context, log)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {log.user_id || 'System'}
                            {log.user_name && (
                              <div className="text-xs text-gray-500">{log.user_name}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => fetchSystemLogs(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => fetchSystemLogs(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages}
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing <span className="font-medium">{((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
                        <span className="font-medium">
                          {Math.min(pagination.page * pagination.limit, pagination.total)}
                        </span>{' '}
                        of <span className="font-medium">{pagination.total}</span> results
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() => fetchSystemLogs(pagination.page - 1)}
                          disabled={pagination.page <= 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Previous
                        </button>
                        
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          const page = i + 1;
                          return (
                            <button
                              key={page}
                              onClick={() => fetchSystemLogs(page)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                page === pagination.page
                                  ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          );
                        })}
                        
                        <button
                          onClick={() => fetchSystemLogs(pagination.page + 1)}
                          disabled={pagination.page >= pagination.totalPages}
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          Next
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Context Modal */}
        <ContextModal />
      </div>
    </div>
  );
};

export default SystemLogs;